import _extends from '@babel/runtime/helpers/extends';
import cx from 'classnames';
import React__default, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import mergeRefs from 'react-merge-refs';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Portal from '../Portal/Portal.js';
import useId from '../../hooks/useId.js';
import { TAB, isKeyboardClick, isEscapeKeyEvent } from '../../utils/keyboard.js';
import { isFunction } from '../../utils/objects.js';
import { getActiveElement } from '../../utils/dom.js';

const OVERLAY_PROPS = {
  containerType: ['default', 'scroll']
};
const BASE_CLASS = 'nui-overlay';
const Classes = {
  OVERLAY: BASE_CLASS,
  BODY_OPEN: `${BASE_CLASS}-open`,
  CONTAINER: `${BASE_CLASS}-container`,
  SCROLL_CONTAINER: `${BASE_CLASS}-scroll-container`,
  CONTENT: `${BASE_CLASS}-content`,
  BACKDROP: `${BASE_CLASS}-backdrop`,
  START_FOCUS_TRAP: `${BASE_CLASS}-start-focus-trap`,
  END_FOCUS_TRAP: `${BASE_CLASS}-end-focus-trap`,
  INLINE: `${BASE_CLASS}--inline`,
  OPEN: `${BASE_CLASS}--open`
};
const overlayPropTypes = {
  /** should the overlay acquire focus on open */
  autoFocus: PropTypes.bool,
  /** class names applied to the backdrop element */
  backdropClassName: PropTypes.string,
  /** HTML props for the backdrop element */
  backdropProps: PropTypes.shape({
    className: PropTypes.string,
    onMouseDown: PropTypes.func
  }),
  /** whether pressing the esc key should invoke onClose */
  canEscapeKeyClose: PropTypes.bool,
  /** whether clicking outside the overlay element should invoke onClose */
  canOutsideClickClose: PropTypes.bool,
  /** element to overlay */
  children: PropTypes.node,
  /** className to add to an overlay wrapper */
  className: PropTypes.string,
  /** overlay positioning and scroll availability */
  containerType: PropTypes.oneOf(OVERLAY_PROPS.containerType),
  /** the element to return focus to on close (ignored if shouldReturnFocusOnClose is false) */
  elementToReturnFocusToOnClose: typeof window !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.shape({}),
  /** keep focus inside overlay when opened */
  enforceFocus: PropTypes.bool,
  /** toggles the visibility of the backdrop behind the contents */
  hasBackdrop: PropTypes.bool,
  /** toggles the visibility of the overlay and its children */
  isOpen: PropTypes.bool.isRequired,
  /** adds Portal and the overlay to the DOM when it's opened for the first time */
  lazy: PropTypes.bool,
  /** invoked when user interaction causes the overlay to close */
  onClose: PropTypes.func,
  /** invoked just after the CSS close transition ends but before the child has been removed from the DOM */
  onClosed: PropTypes.func,
  /** invoked just before the CSS close transition begins on a child */
  onClosing: PropTypes.func,
  /** invoked just after the CSS open transition ends */
  onOpened: PropTypes.func,
  /** invoked just after mounting the child in the DOM but just before the CSS open transition begins */
  onOpening: PropTypes.func,
  /** class names applied to the Portal element */
  portalClassName: PropTypes.string,
  /** container element into which the overlay renders its contents */
  portalContainer: typeof window !== 'undefined' ? PropTypes.instanceOf(Element) : PropTypes.shape({}),
  /** add a class to the body to prevent scrolling of content below the overlay
   * (not necessary if hasBackdrop and usePortal are both true)
  */
  preventBodyScroll: PropTypes.bool,
  /** should the application return focus to the last active element in the document after overlay closes */
  shouldReturnFocusOnClose: PropTypes.bool,
  /** how long (in milliseconds) the overlay's enter/leave transition takes */
  transitionDuration: PropTypes.number,
  /** name of the transition for internal CSSTransition */
  transitionName: PropTypes.string,
  /** whether the overlay should be wrapped in a Portal */
  usePortal: PropTypes.bool
};
const openStack = [];
const getLastOpened = () => openStack[openStack.length - 1];
function Overlay({
  autoFocus = true,
  backdropClassName,
  backdropProps,
  canEscapeKeyClose = true,
  canOutsideClickClose = true,
  children,
  className,
  containerType,
  enforceFocus = true,
  hasBackdrop = true,
  isOpen = false,
  lazy = true,
  onClose,
  onClosed,
  onClosing,
  onOpened,
  onOpening,
  portalClassName,
  portalContainer,
  preventBodyScroll = false,
  shouldReturnFocusOnClose = true,
  transitionDuration = 300,
  transitionName = BASE_CLASS,
  usePortal = true,
  elementToReturnFocusToOnClose
}) {
  const id = useId();
  const [hasEverOpened, setHasEverOpened] = useState(isOpen);
  const containerElement = useRef(null);
  const startFocusTrapElement = useRef(null);
  const endFocusTrapElement = useRef(null);
  const backdropElement = useRef(null);
  const contentElement = useRef(null);
  const isAutoFocusing = useRef(false);
  const lastActiveElementBeforeOpened = useRef();
  const willReturnFocusOnClosing = useRef(typeof window !== 'undefined' && shouldReturnFocusOnClose && elementToReturnFocusToOnClose instanceof HTMLElement);
  const handleTransitionExiting = () => {
    if (willReturnFocusOnClosing.current) {
      elementToReturnFocusToOnClose?.focus();
    }
    onClosing?.(contentElement.current);
  };
  const handleTransitionExited = () => {
    const element = lastActiveElementBeforeOpened.current;
    if (shouldReturnFocusOnClose && !willReturnFocusOnClosing.current && element instanceof HTMLElement) {
      element.focus();
    }
    onClosed?.(contentElement.current);
  };
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    willReturnFocusOnClosing.current = shouldReturnFocusOnClose && elementToReturnFocusToOnClose instanceof HTMLElement;
  }, [shouldReturnFocusOnClose, elementToReturnFocusToOnClose]);
  const bringFocusInsideOverlay = () => {
    // always delay focus manipulation to just before repaint to prevent scroll jumping
    window.requestAnimationFrame(() => {
      if (getLastOpened()?.id !== id) return;

      // container ref may be undefined between component mounting and Portal rendering
      // activeElement may be undefined in some rare cases in IE
      const activeElement = getActiveElement(containerElement.current);
      if (!containerElement.current || !activeElement || !isOpen) return;
      const isFocusOutsideModal = !containerElement.current.contains(activeElement);
      if (isFocusOutsideModal) {
        startFocusTrapElement.current?.focus({
          preventScroll: true
        });
        isAutoFocusing.current = false;
      }
    });
  };
  useEffect(() => {
    const handleDocumentClick = e => {
      // get the actual target even in the Shadow DOM
      const eventTarget = e.composed ? e.composedPath()[0] : e.target;
      const stackIndex = openStack.findIndex(item => item.id === id);
      const isClickInThisOverlayOrDescendant = openStack.slice(stackIndex).some(({
        containerElement: {
          current: elem
        }
      }) =>
      // `elem` is the container of backdrop & content, so clicking on that container
      // should not count as being "inside" the overlay.
      elem && elem.contains(eventTarget) && !elem.isSameNode(eventTarget));
      if (isOpen && !isClickInThisOverlayOrDescendant && canOutsideClickClose) {
        onCloseRef.current?.(e);
      }
    };
    const handleDocumentFocus = e => {
      // get the actual target even in the Shadow DOM
      const eventTarget = e.composed ? e.composedPath()[0] : e.target;
      if (enforceFocus && containerElement.current != null && eventTarget instanceof Node && !containerElement.current.contains(eventTarget)) {
        // prevent default focus behavior (sometimes auto-scrolls the page)
        e.preventDefault();
        e.stopImmediatePropagation();
        bringFocusInsideOverlay();
      }
    };
    if (isOpen) {
      if (openStack.length > 0) {
        document.removeEventListener('focus', getLastOpened().handleDocumentFocus, true);
      }
      openStack.push({
        id,
        autoFocus,
        enforceFocus,
        usePortal,
        hasBackdrop,
        containerElement,
        handleDocumentFocus,
        bringFocusInsideOverlay,
        preventBodyScroll
      });
      if (autoFocus) {
        isAutoFocusing.current = true;
        bringFocusInsideOverlay();
      }
      if (enforceFocus) {
        // Focus events do not bubble, but setting useCapture allows us to listen in and execute
        // our handler before all others
        document.addEventListener('focus', handleDocumentFocus, true);
      }
      if (canOutsideClickClose && !hasBackdrop) {
        document.addEventListener('mousedown', handleDocumentClick);
      }
      if (hasBackdrop && usePortal || preventBodyScroll) {
        // add a class to the body to prevent scrolling of content below the overlay
        document.body.classList.add(Classes.BODY_OPEN);
      }
      lastActiveElementBeforeOpened.current = getActiveElement(containerElement.current);
      setHasEverOpened(true);
    }
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('focus', handleDocumentFocus, true);
      const stackIndex = openStack.findIndex(item => item.id === id);
      if (stackIndex !== -1) {
        openStack.splice(stackIndex, 1);
        if (openStack.length > 0) {
          const lastOpenedOverlay = getLastOpened();
          const shouldIgnoreAutoFocus = willReturnFocusOnClosing.current;
          if (lastOpenedOverlay.autoFocus && !shouldIgnoreAutoFocus) {
            lastOpenedOverlay.bringFocusInsideOverlay();
          }
          if (lastOpenedOverlay.enforceFocus) {
            document.addEventListener('focus', lastOpenedOverlay.handleDocumentFocus, true);
          }
        }
        if (openStack.filter(o => o.usePortal && o.hasBackdrop || o.preventBodyScroll).length === 0) {
          document.body.classList.remove(Classes.BODY_OPEN);
        }
      }
    };
  }, [isOpen]);

  // no reason to render anything at all if we're being truly lazy
  if (lazy && !hasEverOpened) {
    return null;
  }
  const getKeyboardFocusableElements = () => {
    const focusableElements = containerElement.current ? Array.from(
    // Order may not be correct if children elements use tabindex values > 0.
    // Selectors derived from this SO question:
    // https://stackoverflow.com/questions/1599660/which-html-elements-can-receive-focus
    containerElement.current.querySelectorAll(['a[href]:not([tabindex="-1"])', 'button:not([disabled]):not([tabindex="-1"])', 'details:not([tabindex="-1"])', 'input:not([disabled]):not([tabindex="-1"])', 'select:not([disabled]):not([tabindex="-1"])', 'textarea:not([disabled]):not([tabindex="-1"])', '[tabindex]:not([tabindex="-1"])'].join(','))) : [];
    return focusableElements.filter(el => !el.classList.contains(Classes.START_FOCUS_TRAP) && !el.classList.contains(Classes.END_FOCUS_TRAP));
  };
  const handleClick = () => {
    if (enforceFocus) bringFocusInsideOverlay();
  };
  const handleKeyDown = e => {
    if (isKeyboardClick(e)) handleClick();
    if (isEscapeKeyEvent(e) && canEscapeKeyClose) {
      onClose?.(e);
      // prevent other overlays from closing
      e.stopPropagation();
      // prevent browser-specific escape key behavior (Safari exits fullscreen)
      e.preventDefault();
    }
  };
  const handleStartFocusTrapElementFocus = e => {
    if (!enforceFocus || isAutoFocusing.current) return;
    // e.relatedTarget will not be defined if this was a programmatic focus event, as is the
    // case when we call bringFocusInsideOverlay() after a user clicked on the backdrop.
    // Otherwise, we're handling a user interaction, and we should wrap around to the last
    // element in this transition group.
    if (e.relatedTarget && e.relatedTarget instanceof Element && containerElement.current?.contains(e.relatedTarget) && e.relatedTarget !== endFocusTrapElement.current) {
      endFocusTrapElement.current?.focus({
        preventScroll: true
      });
    }
  };
  const handleStartFocusTrapElementKeyDown = e => {
    if (!enforceFocus) return;

    // HACKHACK: https://github.com/palantir/blueprint/issues/4165
    if (e.shiftKey && e.key === TAB) {
      const lastFocusableElement = getKeyboardFocusableElements().pop();
      if (lastFocusableElement) {
        lastFocusableElement.focus();
      } else {
        endFocusTrapElement.current?.focus({
          preventScroll: true
        });
      }
    }
  };
  const handleEndFocusTrapElementFocus = e => {
    if (e.relatedTarget && e.relatedTarget instanceof Element && containerElement.current?.contains(e.relatedTarget) && e.relatedTarget !== startFocusTrapElement.current) {
      const firstFocusableElement = getKeyboardFocusableElements().shift();
      // ensure we don't re-focus an already active element by comparing against e.relatedTarget
      if (!isAutoFocusing.current && firstFocusableElement && firstFocusableElement !== e.relatedTarget) {
        firstFocusableElement.focus();
      } else {
        startFocusTrapElement.current?.focus({
          preventScroll: true
        });
      }
    } else {
      const lastFocusableElement = getKeyboardFocusableElements().pop();
      if (lastFocusableElement) {
        lastFocusableElement.focus();
      } else {
        // Keeps focus within Overlay even if there are no keyboard-focusable children
        startFocusTrapElement.current?.focus({
          preventScroll: true
        });
      }
    }
  };
  const handleBackdropMouseDown = e => {
    if (canOutsideClickClose) onClose?.(e);
    if (enforceFocus) bringFocusInsideOverlay();
    backdropProps?.onMouseDown?.(e);
  };
  const maybeRenderChild = child => {
    const result = isFunction(child) ? child() : child;
    if (result == null) {
      return null;
    }

    // add a special class to each child element that will automatically set the appropriate
    // CSS position mode under the hood.
    const decoratedChild = typeof result === 'object' ? /*#__PURE__*/React__default.cloneElement(result, {
      className: cx(result.props.className, Classes.CONTENT),
      ref: mergeRefs([contentElement, result.ref])
    }) : /*#__PURE__*/React__default.createElement("span", {
      className: Classes.CONTENT,
      ref: contentElement
    }, result);
    return /*#__PURE__*/React__default.createElement(CSSTransition, {
      nodeRef: contentElement,
      classNames: transitionName,
      key: "__child",
      onEntering: isAppearing => onOpening?.(contentElement.current, isAppearing),
      onEntered: isAppearing => onOpened?.(contentElement.current, isAppearing),
      onExiting: handleTransitionExiting,
      onExited: handleTransitionExited,
      timeout: transitionDuration,
      addEndListener: () => {} // eslint-disable-line
    }, decoratedChild);
  };
  const maybeRenderBackdrop = () => {
    if (!hasBackdrop || !isOpen) return null;
    return /*#__PURE__*/React__default.createElement(CSSTransition, {
      nodeRef: backdropElement,
      classNames: transitionName,
      key: "__backdrop",
      timeout: transitionDuration,
      addEndListener: () => {} // eslint-disable-line
    }, /*#__PURE__*/React__default.createElement("div", _extends({}, backdropProps, {
      role: "presentation",
      className: cx(Classes.BACKDROP, backdropClassName, backdropProps?.className),
      onMouseDown: handleBackdropMouseDown,
      ref: backdropElement
    })));
  };
  const renderDummyElement = (key, {
    ref,
    ...props
  }) => /*#__PURE__*/React__default.createElement(CSSTransition, {
    nodeRef: ref,
    classNames: transitionName,
    key: key,
    addEndListener: () => {} // eslint-disable-line
    ,
    timeout: transitionDuration,
    unmountOnExit: true
  }, /*#__PURE__*/React__default.createElement("div", _extends({
    tabIndex: 0
  }, props, {
    ref: ref
  })));
  const childrenWithTransitions = isOpen ? [maybeRenderChild(React__default.Children.only(children))] ?? [] : [];
  const maybeBackdrop = maybeRenderBackdrop();
  if (maybeBackdrop) childrenWithTransitions.unshift(maybeBackdrop);
  if (isOpen && (autoFocus || enforceFocus) && childrenWithTransitions.length > 0) {
    childrenWithTransitions.unshift(renderDummyElement('__start', {
      className: Classes.START_FOCUS_TRAP,
      onFocus: handleStartFocusTrapElementFocus,
      onKeyDown: handleStartFocusTrapElementKeyDown,
      ref: startFocusTrapElement
    }));
    if (enforceFocus) {
      childrenWithTransitions.push(renderDummyElement('__end', {
        className: Classes.END_FOCUS_TRAP,
        onFocus: handleEndFocusTrapElementFocus,
        ref: endFocusTrapElement
      }));
    }
  }
  const containerClasses = cx(Classes.OVERLAY, {
    [Classes.OPEN]: isOpen,
    [Classes.INLINE]: !usePortal,
    [Classes.CONTAINER]: containerType === 'default',
    [Classes.SCROLL_CONTAINER]: containerType === 'scroll'
  }, className);
  const transitionGroup =
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
  React__default.createElement("div", {
    "aria-live": "polite",
    className: containerClasses,
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    ref: containerElement
  }, /*#__PURE__*/React__default.createElement(TransitionGroup, {
    component: null,
    appear: true
  }, childrenWithTransitions));
  return usePortal ? /*#__PURE__*/React__default.createElement(Portal, {
    className: portalClassName,
    container: portalContainer
  }, transitionGroup) : transitionGroup;
}
Overlay.propTypes = overlayPropTypes;
Overlay.displayName = 'NuiOverlay';

export { OVERLAY_PROPS, Overlay as default };
